import {
	BigNumber,
	LockingScript,
	OP,
	P2PKH,
	type PrivateKey,
	Script,
	type Transaction,
	TransactionSignature,
	UnlockingScript,
	Utils,
} from "@treechat/bsv-sdk";
import { toHex } from "../utils/strings";
import type { Inscription } from "../types";

export const ORD_LOCK_PAYOUT_LAYOUT_V1 = 1 as const;
export const ORD_LOCK_PAYOUT_LAYOUT_V2 = 2 as const;
export const ORD_LOCK_V2_OUTPUT_INDEXES = {
	ordinal: 0,
	seller: 1,
	fee: 2,
	trailing: 3,
} as const;

export type OrdLockPayoutLayoutVersion =
	| typeof ORD_LOCK_PAYOUT_LAYOUT_V1
	| typeof ORD_LOCK_PAYOUT_LAYOUT_V2;

export type OrdLockCommittedOutput = {
	satoshis: number;
	lockingScript: Script;
};

export type ParsedOrdLockCommittedOutput = {
	satoshis: number;
	lockingScript: LockingScript;
	serialized: number[];
};

const MAX_SAFE_INTEGER_BN = new BigNumber(Number.MAX_SAFE_INTEGER);

const assertSafeSatoshis = (satoshis: number): void => {
	if (!Number.isSafeInteger(satoshis) || satoshis < 1) {
		throw new Error("OrdLock committed output satoshis must be a positive safe integer");
	}
};

const readCanonicalVarInt = (reader: Utils.Reader): number => {
	if (reader.eof()) {
		throw new Error("OrdLock payout output is missing its script length");
	}

	const prefix = reader.readUInt8();
	if (prefix < 0xfd) return prefix;

	if (prefix === 0xfd) {
		if (reader.bin.length - reader.pos < 2) {
			throw new Error("OrdLock payout output has a truncated script length");
		}
		const length = reader.readUInt16LE();
		if (length < 0xfd) throw new Error("OrdLock payout output uses a non-canonical script length");
		return length;
	}

	if (prefix === 0xfe) {
		if (reader.bin.length - reader.pos < 4) {
			throw new Error("OrdLock payout output has a truncated script length");
		}
		const length = reader.readUInt32LE();
		if (length <= 0xffff) {
			throw new Error("OrdLock payout output uses a non-canonical script length");
		}
		return length;
	}

	if (reader.bin.length - reader.pos < 8) {
		throw new Error("OrdLock payout output has a truncated script length");
	}
	const length = reader.readUInt64LEBn();
	if (length.lte(new BigNumber(0xffffffff))) {
		throw new Error("OrdLock payout output uses a non-canonical script length");
	}
	if (length.gt(MAX_SAFE_INTEGER_BN)) {
		throw new Error("OrdLock payout output script is too large");
	}
	return length.toNumber();
};

const readCommittedOutput = (reader: Utils.Reader): ParsedOrdLockCommittedOutput => {
	const start = reader.pos;
	if (reader.bin.length - reader.pos < 9) {
		throw new Error("OrdLock payout output is truncated");
	}

	const satoshisBn = reader.readUInt64LEBn();
	if (satoshisBn.gt(MAX_SAFE_INTEGER_BN)) {
		throw new Error("OrdLock payout output satoshis exceed the safe integer range");
	}
	const satoshis = satoshisBn.toNumber();
	assertSafeSatoshis(satoshis);

	const scriptLength = readCanonicalVarInt(reader);
	if (scriptLength < 1) throw new Error("OrdLock payout output locking script is empty");
	if (reader.bin.length - reader.pos < scriptLength) {
		throw new Error("OrdLock payout output locking script is truncated");
	}
	const script = reader.read(scriptLength);

	return {
		satoshis,
		lockingScript: LockingScript.fromBinary(script),
		serialized: reader.bin.slice(start, reader.pos),
	};
};

const committedOutputCount = (layoutVersion: number): number => {
	if (layoutVersion === ORD_LOCK_PAYOUT_LAYOUT_V1) return 1;
	if (layoutVersion === ORD_LOCK_PAYOUT_LAYOUT_V2) return 2;
	throw new Error(`Unsupported OrdLock payout layout version: ${layoutVersion}`);
};

/** Serializes one output exactly as BIP143 hashOutputs does, without an output count. */
export const serializeOrdLockOutput = (output: OrdLockCommittedOutput): number[] => {
	assertSafeSatoshis(output.satoshis);
	const lockingScript = output.lockingScript.toBinary();
	if (lockingScript.length < 1) throw new Error("OrdLock committed output locking script is empty");
	return OrdLock.buildOutput(output.satoshis, lockingScript);
};

/**
 * Strictly parses the opaque payout blob embedded in OrdLock. V1 contains one
 * seller output. V2 contains the seller output followed by the Treechat fee
 * output. No bytes may remain after the layout's exact output count.
 */
export const parseOrdLockPayout = (
	payout: ArrayLike<number>,
	layoutVersion: number,
): ParsedOrdLockCommittedOutput[] => {
	const bytes = Array.from(payout);
	if (bytes.some((byte) => !Number.isInteger(byte) || byte < 0 || byte > 0xff)) {
		throw new Error("OrdLock payout must contain only bytes");
	}
	const reader = new Utils.Reader(bytes);
	const outputs = Array.from({ length: committedOutputCount(layoutVersion) }, () =>
		readCommittedOutput(reader),
	);
	if (!reader.eof()) throw new Error("OrdLock payout contains unexpected trailing bytes");
	return outputs;
};

export const serializeOrdLockPayout = (
	layoutVersion: number,
	outputs: OrdLockCommittedOutput[],
): number[] => {
	const expectedCount = committedOutputCount(layoutVersion);
	if (outputs.length !== expectedCount) {
		throw new Error(
			`OrdLock payout layout v${layoutVersion} requires exactly ${expectedCount} committed outputs`,
		);
	}
	const payout = outputs.flatMap(serializeOrdLockOutput);
	// Keep serialization and parsing as one canonical boundary.
	parseOrdLockPayout(payout, layoutVersion);
	return payout;
};

/** Seller proceeds and Treechat's fee, in their consensus-enforced order. */
export const serializeFeeAwareOrdLockPayout = (
	sellerOutput: OrdLockCommittedOutput,
	feeOutput: OrdLockCommittedOutput,
): number[] =>
	serializeOrdLockPayout(ORD_LOCK_PAYOUT_LAYOUT_V2, [sellerOutput, feeOutput]);

const serializedOutputsEqual = (
	actual: { satoshis?: number; lockingScript: Script },
	expected: ParsedOrdLockCommittedOutput,
): boolean => {
	if (actual.satoshis !== expected.satoshis) return false;
	return actual.lockingScript.toHex() === expected.lockingScript.toHex();
};

export const oLockPrefix =
	"2097dfd76851bf465e8f715593b217714858bbe9570ff3bd5e33840a34e20ff0262102ba79df5f8ae7604a9830f03c7933028186aede0675a16f025dc4f8be8eec0382201008ce7480da41702918d1ec8e6849ba32b4d65b1e40dc669c31a1e6306b266c0000";
export const oLockSuffix =
	"615179547a75537a537a537a0079537a75527a527a7575615579008763567901c161517957795779210ac407f0e4bd44bfc207355a778b046225a7068fc59ee7eda43ad905aadbffc800206c266b30e6a1319c66dc401e5bd6b432ba49688eecd118297041da8074ce081059795679615679aa0079610079517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01007e81517a75615779567956795679567961537956795479577995939521414136d08c5ed2bf3ba048afe6dcaebafeffffffffffffffffffffffffffffff00517951796151795179970079009f63007952799367007968517a75517a75517a7561527a75517a517951795296a0630079527994527a75517a6853798277527982775379012080517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01205279947f7754537993527993013051797e527e54797e58797e527e53797e52797e57797e0079517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a756100795779ac517a75517a75517a75517a75517a75517a75517a75517a75517a7561517a75517a756169587951797e58797eaa577961007982775179517958947f7551790128947f77517a75517a75618777777777777777777767557951876351795779a9876957795779ac777777777777777767006868";

// BEGIN GENERATED TREECHAT ORDLOCK V2
// Generated by contracts/treechatOrdLockV2: npm ci && npm run compile.
export const oLockV2Prefix =
	"0176018801a901ac2097dfd76851bf465e8f715593b217714858bbe9570ff3bd5e33840a34e20ff0262102ba79df5f8ae7604a9830f03c7933028186aede0675a16f025dc4f8be8eec0382201008ce7480da41702918d1ec8e6849ba32b4d65b1e40dc669c31a1e6306b266c000000";
export const oLockV2Suffix =
	"78557a75547a547a547a547a76547a7572537a6d5a790087635b7901c17858795879210ac407f0e4bd44bfc207355a778b046225a7068fc59ee7eda43ad905aadbffc800206c266b30e6a1319c66dc401e5bd6b432ba49688eecd118297041da8074ce08105a7956795679aa7676517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01007e817757795679567956795679537956795479577995939521414136d08c5ed2bf3ba048afe6dcaebafeffffffffffffffffffffffffffffff006e6e9776009f636e936776687777777b757c6e5296a0636e7c947b757c6853798277527982775379012080517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01205279947f77545379935279930130787e527e54797e58797e527e53797e52797e57797e6b6d6d6d6d6d6d6c765779ac6b6d6d6d6d6d6c77695b797682776e58947f75780128947f6b6d6d6c5d79827701149d5d795a7959797e01147e787e5a797e58797e77517658805279768277007802fd009f6378516e8b80767682778c7f75007f6b6d6d6c776778030000019f6301fd5279526e8b80767682778c7f75007f6b6d6d6c7e7767780500000000019f6301fe5279546e8b80767682778c7f75007f6b6d6d6c7e776778090000000000000000019f6301ff5279586e8b80767682778c7f75007f6b6d6d6c7e77686868687653797e7777777e77777653797e5e797eaa5279876b6d6d6d6d6d6d6d6c77675a7951876352795c79a9885c795c79ac6b6d6d6d6d6d6d6c7767006868";
// END GENERATED TREECHAT ORDLOCK V2

/**
 * OrdLock class implementing ScriptTemplate.
 *
 * This class provides methods for interacting with OrdinalLock contract 
 */
export default class OrdLock {
	/**
	 * Creates a 1Sat Ordinal Lock script
	 *
	 * @param {string} ordAddress - An address which can cancel listing.
	 * @param {string} payAddress - Address which is paid on purchase
	 * @param {number} price - Listing price in satoshis
	 * @returns {LockingScript} - A P2PKH locking script.
	 */
	lock(
		ordAddress: string,
		payAddress: string,
		price: number,
		inscription?: Inscription,
	): Script {
		const payPkh = Utils.fromBase58Check(payAddress).data as number[];
		const payout = OrdLock.buildOutput(price, new P2PKH().lock(payPkh).toBinary());
		return this.lockWithPayout(ordAddress, payout, inscription);
	}

	/**
	 * Creates Treechat's hardened fee-enforcing contract. Its purchase branch
	 * constructs the canonical one-satoshi buyer output before appending the
	 * committed seller and Treechat outputs, fixing both payout boundaries.
	 */
	lockV2(
		ordAddress: string,
		sellerOutput: OrdLockCommittedOutput,
		feeOutput: OrdLockCommittedOutput,
		inscription?: Inscription,
	): Script {
		return this.lockWithPayout(
			ordAddress,
			serializeFeeAwareOrdLockPayout(sellerOutput, feeOutput),
			inscription,
			ORD_LOCK_PAYOUT_LAYOUT_V2,
		);
	}

	private lockWithPayout(
		ordAddress: string,
		payout: number[],
		inscription?: Inscription,
		layoutVersion: OrdLockPayoutLayoutVersion = ORD_LOCK_PAYOUT_LAYOUT_V1,
	): Script {
		const cancelPkh = Utils.fromBase58Check(ordAddress).data as number[];

		let script = new Script()
		if (inscription?.dataB64 !== undefined && inscription?.contentType !== undefined) {
			const ordHex = toHex("ord");
			const fsBuffer = Buffer.from(inscription.dataB64, "base64");
			const fileHex = fsBuffer.toString("hex").trim();
			if (!fileHex) {
				throw new Error("Invalid file data");
			}
			const fileMediaType = toHex(inscription.contentType);
			if (!fileMediaType) {
				throw new Error("Invalid media type");
			}
			script = Script.fromASM(`OP_0 OP_IF ${ordHex} OP_1 ${fileMediaType} OP_0 ${fileHex} OP_ENDIF`);
		}

		return script.writeScript(Script.fromHex(layoutVersion === ORD_LOCK_PAYOUT_LAYOUT_V2 ? oLockV2Prefix : oLockPrefix))
			.writeBin(cancelPkh)
			.writeBin(payout)
			.writeScript(Script.fromHex(layoutVersion === ORD_LOCK_PAYOUT_LAYOUT_V2 ? oLockV2Suffix : oLockSuffix))
	}

	cancelListing(
		privateKey: PrivateKey,
		signOutputs: 'all' | 'none' | 'single' = 'all',
		anyoneCanPay = false,
		sourceSatoshis?: number,
		lockingScript?: Script
	): {
		sign: (tx: Transaction, inputIndex: number) => Promise<UnlockingScript>
		estimateLength: () => Promise<number>
	} {
		const p2pkh = new P2PKH().unlock(privateKey, signOutputs, anyoneCanPay, sourceSatoshis, lockingScript)
		return {
			sign: async (tx: Transaction, inputIndex: number) => {
				return (await p2pkh.sign(tx, inputIndex)).writeOpCode(OP.OP_1)
			},
			estimateLength: async () => {
				return 107
			}
		}
	}

	/** The fee-aware cancel path always commits the complete input and output set. */
	cancelListingV2(
		privateKey: PrivateKey,
		sourceSatoshis?: number,
		lockingScript?: Script,
	): {
		sign: (tx: Transaction, inputIndex: number) => Promise<UnlockingScript>
		estimateLength: () => Promise<number>
	} {
		if (sourceSatoshis !== undefined && sourceSatoshis !== 1) {
			throw new Error("Fee-aware OrdLock listings must contain exactly one satoshi");
		}
		const cancel = this.cancelListing(privateKey, "all", false, sourceSatoshis, lockingScript);
		return {
			sign: async (tx: Transaction, inputIndex: number) => {
				if (inputIndex !== 0) {
					throw new Error("Fee-aware OrdLock must be transaction input 0");
				}
				if (tx.outputs[0]?.satoshis !== 1) {
					throw new Error("Fee-aware OrdLock cancellation requires a one-satoshi output 0");
				}
				const input = tx.inputs[inputIndex];
				const actualSourceSatoshis =
					sourceSatoshis ??
					input.sourceTransaction?.outputs[input.sourceOutputIndex]?.satoshis;
				if (actualSourceSatoshis !== 1) {
					throw new Error("Fee-aware OrdLock listings must contain exactly one satoshi");
				}
				return cancel.sign(tx, inputIndex);
			},
			estimateLength: cancel.estimateLength,
		};
	}

	purchaseListing(
		sourceSatoshis?: number,
		lockingScript?: Script
	): {
		sign: (tx: Transaction, inputIndex: number) => Promise<UnlockingScript>
		estimateLength: (tx: Transaction, inputIndex: number) => Promise<number>
	} {
		const purchase = {
			sign: async (tx: Transaction, inputIndex: number) => {
				if (tx.outputs.length < 2) {
					throw new Error("Malformed transaction")
				}
				const script = new UnlockingScript()
					.writeBin(OrdLock.buildOutput(
						tx.outputs[0].satoshis || 0,
						tx.outputs[0].lockingScript.toBinary()
					))
				if (tx.outputs.length > 2) {
					const writer = new Utils.Writer()
					for (const output of tx.outputs.slice(2)) {
						writer.write(OrdLock.buildOutput(output.satoshis || 0, output.lockingScript.toBinary()))
					}
					script.writeBin(writer.toArray())
				} else {
					script.writeOpCode(OP.OP_0)
				}

				const input = tx.inputs[inputIndex]
				let sourceSats = sourceSatoshis as number
				if (!sourceSats && input.sourceTransaction) {
					sourceSats = input.sourceTransaction.outputs[input.sourceOutputIndex].satoshis as number
				} else if (!sourceSatoshis) {
					throw new Error("sourceTransaction or sourceSatoshis is required")
				}

				const sourceTXID = (input.sourceTXID || input.sourceTransaction?.id('hex')) as string
				let subscript = lockingScript as LockingScript
				if (!subscript) {
					subscript = input.sourceTransaction?.outputs[input.sourceOutputIndex].lockingScript as LockingScript
				}
				const preimage = TransactionSignature.format({
					sourceTXID,
					sourceOutputIndex: input.sourceOutputIndex,
					sourceSatoshis: sourceSats,
					transactionVersion: tx.version,
					otherInputs: [],
					inputIndex,
					outputs: tx.outputs,
					inputSequence: input.sequence || 0xffffffff,
					subscript,
					lockTime: tx.lockTime,
					scope: TransactionSignature.SIGHASH_ALL |
						TransactionSignature.SIGHASH_ANYONECANPAY |
						TransactionSignature.SIGHASH_FORKID
				});

				return script.writeBin(preimage).writeOpCode(OP.OP_0)
			},
			estimateLength: async (tx: Transaction, inputIndex: number) => {
				return (await purchase.sign(tx, inputIndex)).toBinary().length
			}
		}
		return purchase
	}

	/**
	 * Purchases a fee-aware v2 listing. Outputs 1 and 2 must exactly equal the
	 * two committed outputs. Output 0 is the one-satoshi ordinal destination;
	 * only outputs from index 3 onward are supplied as covenant trailing bytes.
	 */
	purchaseListingV2(
		payout: ArrayLike<number>,
		sourceSatoshis?: number,
		lockingScript?: Script,
	): {
		sign: (tx: Transaction, inputIndex: number) => Promise<UnlockingScript>
		estimateLength: (tx: Transaction, inputIndex: number) => Promise<number>
	} {
		const committedOutputs = parseOrdLockPayout(payout, ORD_LOCK_PAYOUT_LAYOUT_V2);
		if (sourceSatoshis !== undefined && sourceSatoshis !== 1) {
			throw new Error("Fee-aware OrdLock listings must contain exactly one satoshi");
		}

		const purchase = {
			sign: async (tx: Transaction, inputIndex: number) => {
				if (inputIndex !== 0) {
					throw new Error("Fee-aware OrdLock must be transaction input 0");
				}
				if (
					tx.outputs.length < ORD_LOCK_V2_OUTPUT_INDEXES.trailing ||
					tx.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.ordinal].satoshis !== 1
				) {
					throw new Error("Fee-aware OrdLock purchase requires a one-satoshi output 0");
				}
				for (const [offset, expected] of committedOutputs.entries()) {
					const outputIndex = ORD_LOCK_V2_OUTPUT_INDEXES.seller + offset;
					if (!serializedOutputsEqual(tx.outputs[outputIndex], expected)) {
						throw new Error(
							`Fee-aware OrdLock committed output ${outputIndex} does not match its payout`,
						);
					}
				}

				const buyerScript = tx.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.ordinal].lockingScript;
				if (!/^76a914[0-9a-f]{40}88ac$/.test(buyerScript.toHex())) {
					throw new Error("Fee-aware OrdLock ordinal output must be canonical P2PKH");
				}
				// The contract receives only the 20-byte key hash. It builds the
				// complete output itself, so payout bytes cannot hide in its script.
				const script = new UnlockingScript().writeBin(buyerScript.toBinary().slice(3, 23));
				if (tx.outputs.length > ORD_LOCK_V2_OUTPUT_INDEXES.trailing) {
					const writer = new Utils.Writer();
					for (const output of tx.outputs.slice(ORD_LOCK_V2_OUTPUT_INDEXES.trailing)) {
						writer.write(
							OrdLock.buildOutput(
								output.satoshis || 0,
								output.lockingScript.toBinary(),
							),
						);
					}
					script.writeBin(writer.toArray());
				} else {
					script.writeOpCode(OP.OP_0);
				}

				const input = tx.inputs[inputIndex];
				const actualSourceSatoshis =
					sourceSatoshis ??
					input.sourceTransaction?.outputs[input.sourceOutputIndex]?.satoshis;
				if (actualSourceSatoshis !== 1) {
					throw new Error("Fee-aware OrdLock listings must contain exactly one satoshi");
				}
				const sourceTXID = input.sourceTXID || input.sourceTransaction?.id("hex");
				if (!sourceTXID) throw new Error("sourceTransaction or sourceTXID is required");
				const subscript =
					lockingScript ??
					input.sourceTransaction?.outputs[input.sourceOutputIndex]?.lockingScript;
				if (!subscript) throw new Error("sourceTransaction or lockingScript is required");

				const preimage = TransactionSignature.format({
					sourceTXID,
					sourceOutputIndex: input.sourceOutputIndex,
					sourceSatoshis: actualSourceSatoshis,
					transactionVersion: tx.version,
					otherInputs: [],
					inputIndex,
					outputs: tx.outputs,
					inputSequence: input.sequence ?? 0xffffffff,
					subscript,
					lockTime: tx.lockTime,
					scope:
						TransactionSignature.SIGHASH_ALL |
						TransactionSignature.SIGHASH_ANYONECANPAY |
						TransactionSignature.SIGHASH_FORKID,
				});

				return script.writeBin(preimage).writeOpCode(OP.OP_0);
			},
			estimateLength: async (tx: Transaction, inputIndex: number) =>
				(await purchase.sign(tx, inputIndex)).toBinary().length,
		};
		return purchase;
	}

	static buildOutput(satoshis: number, script: number[]): number[] {
		const writer = new Utils.Writer()
		writer.writeUInt64LEBn(new BigNumber(satoshis))
		writer.writeVarIntNum(script.length)
		writer.write(script)
		return writer.toArray()
	}
}
